import { useEffect, useRef, useState } from "react";
import { Sparkles as InsightsIcon, BarChart3, Coffee, Activity, Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import { setLayerActive, useLayerOffset } from "@/lib/pr30/zeroOverlapPolicy";

/**
 * PR-22 — Profile Quick Actions.
 * Five buttons that route the user to the right module:
 *   Rewards · Stats · Halftime Hub · Polls Center · Match AI
 * Each emits `profile_quick_action_executed` for analytics and uses the
 * existing PR-18 global event bus so handlers in routes/index.tsx pick
 * them up without prop drilling.
 */
export interface ProfileQuickActionsProps {
  onNavigateChat?: () => void;
}

function emit(name: string) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(name));
  }
}

export function ProfileQuickActions({ onNavigateChat }: ProfileQuickActionsProps) {
  const { t } = useLanguage();
  const fire = (action: string, fn: () => void) => {
    void trackEvent("profile_quick_action_executed", { action, surface: "profile_tab" });
    fn();
  };
  const offset = useLayerOffset("profile_quick");
  const containerRef = useRef<HTMLDivElement>(null);
  const [carousel, setCarousel] = useState(false);

  useEffect(() => {
    setLayerActive("profile_quick", true);
    return () => setLayerActive("profile_quick", false);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      // Switch to horizontal carousel if width is too tight for 5 buttons
      const next = el.clientWidth < 420;
      setCarousel((prev) => {
        if (prev === next) return prev;
        void trackEvent("profile_quick_actions_reflowed", { layout: next ? "carousel" : "grid", width: el.clientWidth });
        return next;
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="rounded-2xl glass p-3 transition-transform"
      style={{ maxHeight: 96, transform: offset ? `translateY(-${offset}px)` : undefined }}
    >
      <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
        Match Hub
      </div>
      <div
        className={
          carousel
            ? "flex gap-2 overflow-x-auto snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "grid grid-cols-2 gap-2 sm:grid-cols-5"
        }
        style={carousel ? { maxHeight: 72 } : undefined}
      >
        <QuickButton
          icon={<InsightsIcon className="h-4 w-4 text-[oklch(0.85_0.18_85)]" />}
          label={t("qa_dock_rewards")}
          onClick={() => fire("rewards", () => emit("ss2026:open-rewards"))}
        />
        <QuickButton
          icon={<BarChart3 className="h-4 w-4 text-primary" />}
          label={t("qa_dock_stats")}
          onClick={() => fire("stats", () => emit("ss2026:open-stats"))}
        />
        <QuickButton
          icon={<Coffee className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />}
          label={t("qa_dock_halftime")}
          onClick={() => fire("halftime", () => emit("ss2026:open-halftime"))}
        />
        <QuickButton
          icon={<Activity className="h-4 w-4 text-[oklch(0.72_0.18_220)]" />}
          label={t("qa_dock_polls")}
          onClick={() =>
            fire("polls", () => {
              void trackEvent("polls_center_opened", { surface: "profile_quick_action" });
              emit("ss2026:open-trivia");
            })
          }
        />
        <QuickButton
          icon={<Sparkles className="h-4 w-4 text-[oklch(0.78_0.18_70)]" />}
          label="IA del Partido"
          onClick={() =>
            fire("match_ai", () => {
              void trackEvent("match_ai_opened_from_profile", { surface: "profile_quick_action" });
              onNavigateChat?.();
            })
          }
        />
      </div>
    </div>
  );
}

function QuickButton({
  icon, label, onClick,
}: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-xl border border-border/60 bg-background/60 p-2 text-[11px] font-bold transition hover:bg-accent min-w-[72px] max-h-[60px]"
    >
      {icon}
      <span className="text-center leading-tight">{label}</span>
    </button>
  );
}